// Chart › Timeline, below the lanes: a trend card for every test with an H, L or critical result in the selected
// range (Andrew's ruling (a)), two to a row, and the inline "Compare results" card under them, as the demo draws it
// (scrips-clinician-demo ?view=timeline, 4 Nov): the first test is compared by default, "Add a second result" picks one
// more. Mounted by ChartTimeline (chart-timeline.tsx), which owns the `chartTimeline` switch: this component reads no switch.
// Data: the Labs tab's own reads (useLabResultGroups) for the flagged rows, and the per-test history
// (GET /api/Patients/{id}/LabObservations) for each line. No result is made up; none flagged = one quiet line.
import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverItem,
  AnchoredPopoverTitle,
  AnchoredPopoverTrigger,
  Button,
} from '@scripsteam/scrips-signal-ds';
import { flaggedTestCards, type LabTrendCardModel, type TimelineRange } from '@/patients/lib/lab-trend-cards';
import { ChartSectionHeader } from './chart-section-header';
import { useLabResultGroups } from './labs/use-lab-result-groups';
import { LabTrendCard } from './lab-trends/lab-trend-card';
import { LabComparePanel } from './lab-trends/lab-compare-panel';

/** At most this many tests in Compare results. */
export const MAX_COMPARE = 2;

export interface LabTrendSectionProps {
  /** The chart's patient GUID. */
  patientId: string;
  /** ISO dates, both inclusive; `from` null = all time. */
  range: TimelineRange;
}

/** The inline Compare card: the picked tests as chips, "Add a second result", then the chart. */
function CompareCard({
  patientId,
  cards,
  picked,
  labsByRoot,
  range,
  onPick,
  onRemove,
}: {
  patientId: string;
  cards: LabTrendCardModel[];
  picked: LabTrendCardModel[];
  labsByRoot: ReadonlyMap<string, string>;
  range: TimelineRange;
  onPick: (key: string) => void;
  onRemove: (key: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const others = cards.filter((c) => !picked.some((p) => p.key === c.key));
  const units = [...new Set(picked.map((c) => c.row.unit).filter(Boolean))].join(', ');
  return (
    <section data-testid="lab-compare-card" aria-label="Compare results" className="flex flex-col gap-3 rounded-lg border border-border-card bg-surface-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-text-secondary">{`Compare results${units ? ` · ${units}` : ''}`}</span>
        <div role="group" aria-label="Tests compared" className="flex flex-wrap items-center gap-2 rounded-md border border-border-card px-2 py-1">
          {picked.map((c) => (
            <span key={c.key} data-testid="lab-compare-chip" className="inline-flex items-center gap-1 text-sm text-text-primary">
              {c.row.display}
              {picked.length > 1 ? (
                <Button variant="tertiary" size="sm" icon={<X />} aria-label={`Remove ${c.row.display}`} onClick={() => onRemove(c.key)} />
              ) : null}
            </span>
          ))}
          {picked.length < MAX_COMPARE && others.length > 0 ? (
            <AnchoredPopover open={adding} onOpenChange={setAdding}>
              <AnchoredPopoverTrigger render={<Button variant="tertiary" size="sm">Add a second result</Button>} />
              <AnchoredPopoverContent aria-label="Add a second result">
                <AnchoredPopoverTitle>Add a second result</AnchoredPopoverTitle>
                {others.map((c) => (
                  <AnchoredPopoverItem
                    key={c.key}
                    onClick={() => {
                      onPick(c.key);
                      setAdding(false);
                    }}
                  >
                    {c.row.unit ? `${c.row.display} · ${c.row.unit}` : c.row.display}
                  </AnchoredPopoverItem>
                ))}
              </AnchoredPopoverContent>
            </AnchoredPopover>
          ) : null}
        </div>
      </div>
      <LabComparePanel patientId={patientId} cards={picked} labsByRoot={labsByRoot} range={range} />
    </section>
  );
}

export function LabTrendSection({ patientId, range }: LabTrendSectionProps) {
  const { available, groups, externalGroups, labsByRoot, failed } = useLabResultGroups(patientId);
  const cards = useMemo(
    () => flaggedTestCards([...groups, ...externalGroups], range),
    [groups, externalGroups, range],
  );
  const [picked, setPicked] = useState<string[]>([]);

  // Only cards still on screen stay picked (the range changed, or a result was withdrawn); none picked = the first card, as
  // the demo opens Compare on its first test.
  const stillPicked = cards.filter((c) => picked.includes(c.key));
  const pickedCards = stillPicked.length > 0 ? stillPicked : cards.slice(0, 1);
  const pick = (key: string) => setPicked([...pickedCards.map((c) => c.key), key].slice(-MAX_COMPARE));
  const remove = (key: string) => setPicked(pickedCards.map((c) => c.key).filter((k) => k !== key));

  // Before the results store answers, render nothing: the Timeline lanes above say what is there.
  if (!available && failed.length === 0) return null;

  return (
    <section data-testid="lab-trend-section" aria-label="Lab trends" className="@container flex flex-col gap-3">
      <ChartSectionHeader title="Lab trends" count={cards.length} showAdd={false} />

      {failed.length > 0 ? (
        <p role="alert" className="text-xs text-status-error">
          Some lab results could not be loaded, so this list may be incomplete.
        </p>
      ) : null}

      {cards.length === 0 ? (
        <p data-testid="lab-trend-empty" className="text-sm text-text-hint">
          No high, low or critical lab results in this period.
        </p>
      ) : (
        <>
          {/* Two to a row by the section's own width, not the window's: the encounter's chart column (~370–520 px) keeps one. */}
          <div data-testid="lab-trend-grid" className="grid grid-cols-1 gap-3 @2xl:grid-cols-2">
            {cards.map((card) => (
              <LabTrendCard key={card.key} patientId={patientId} card={card} labsByRoot={labsByRoot} range={range} />
            ))}
          </div>
          <CompareCard
            patientId={patientId}
            cards={cards}
            picked={pickedCards}
            labsByRoot={labsByRoot}
            range={range}
            onPick={pick}
            onRemove={remove}
          />
        </>
      )}
    </section>
  );
}
